<script setup lang="ts">
import { computed, ref, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import isotypeSrc from '@/assets/isotype.svg'
import logoWhiteSrc from '@/assets/logo-white.svg'
import managementReportsCopy from '@/locales/managementReports.es.json'

const route = useRoute()
const router = useRouter()
const ui = useUiStore()
const auth = useAuthStore()

function handleNavClick(e: MouseEvent, path: string) {
  e.preventDefault()
  if (isActive(path)) {
    router.push({ path, query: { _r: String(Date.now()) } }).catch(() => {})
  } else {
    router.push(path)
  }
}

const navItems = [
  { name: 'Inicio', path: '/', icon: 'home' },
  { name: 'Contratos', path: '/contracts', icon: 'description' },
  { name: 'Proveedores', path: '/suppliers', icon: 'business' },
  { name: 'Planes de acción', path: '/action-plans', icon: 'assignment' },
  { name: managementReportsCopy.title, path: '/management-reports', icon: 'summarize' },
  { name: 'Exportaciones', path: '/exports', icon: 'download' },
]

const adminNavItems = [
  { name: 'Familias KPI', path: '/kpi-families', icon: 'folder' },
  { name: 'Etiquetas KPI', path: '/kpi-labels', icon: 'label' },
  { name: 'Usuarios', path: '/settings/users', icon: 'group' },
  { name: 'Configuración', path: '/settings/config', icon: 'settings' },
]

const userNavItems = [
  { name: 'Preferencias', path: '/settings/preferences', icon: 'person' },
]

function isActive(path: string): boolean {
  if (path === '/') return route.path === '/'
  return route.path.startsWith(path)
}

// Hover expand/collapse with debounce
const hovered = ref(false)
let collapseTimeout: ReturnType<typeof setTimeout> | null = null

function onMouseEnter() {
  if (collapseTimeout) {
    clearTimeout(collapseTimeout)
    collapseTimeout = null
  }
  hovered.value = true
}

function onMouseLeave() {
  collapseTimeout = setTimeout(() => {
    hovered.value = false
  }, 150)
}

onBeforeUnmount(() => {
  if (collapseTimeout) clearTimeout(collapseTimeout)
})

const isExpanded = computed(() => ui.sidebarOpen || hovered.value)

const sidebarClasses = computed(() => {
  if (ui.sidebarOpen) return 'translate-x-0'
  if (hovered.value) return '-translate-x-full md:translate-x-0'
  return '-translate-x-full md:translate-x-0 md:w-20'
})
</script>

<template>
  <aside
    data-testid="sidebar"
    :class="sidebarClasses"
    class="fixed inset-y-0 left-0 z-30 flex w-64 flex-col bg-indigo-700 text-white transition-all duration-300 ease-in-out"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <!-- Logo / Brand -->
    <div :class="['flex h-16 items-center', isExpanded ? 'gap-3 px-6' : 'justify-center px-2']">
      <img
        :src="isotypeSrc"
        alt="Plutto"
        class="h-7 w-7"
      >
      <span
        v-if="isExpanded"
        class="text-lg font-semibold tracking-tight"
      >
        SPM
      </span>
    </div>

    <!-- Navigation -->
    <nav class="mt-4 flex-1 space-y-1 px-3">
      <a
        v-for="item in navItems"
        :key="item.path"
        :href="item.path"
        :title="isExpanded ? undefined : item.name"
        :class="[
          'flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap overflow-hidden',
          isExpanded ? 'gap-3' : 'justify-center',
          isActive(item.path)
            ? 'bg-white/15 text-white'
            : 'text-white/70 hover:bg-white/10 hover:text-white',
        ]"
        @click="handleNavClick($event, item.path)"
      >
        <span class="plutto-icon plutto-icon--outlined shrink-0 text-xl">{{ item.icon }}</span>
        <span v-if="isExpanded">{{ item.name }}</span>
      </a>

      <!-- Reajustes pendientes (Admin + Gestor only) -->
      <a
        v-if="auth.isAdmin || auth.isGestor"
        href="/reajustes-pendientes"
        :title="isExpanded ? undefined : 'Reajustes pendientes'"
        :class="[
          'flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap overflow-hidden',
          isExpanded ? 'gap-3' : 'justify-center',
          isActive('/reajustes-pendientes')
            ? 'bg-white/15 text-white'
            : 'text-white/70 hover:bg-white/10 hover:text-white',
        ]"
        @click="handleNavClick($event, '/reajustes-pendientes')"
      >
        <span class="plutto-icon plutto-icon--outlined shrink-0 text-xl">currency_exchange</span>
        <span v-if="isExpanded">Reajustes pendientes</span>
      </a>

      <!-- Mediciones pendientes (all roles) -->
      <a
        href="/measurement-requests"
        :title="isExpanded ? undefined : 'Mediciones pendientes'"
        :class="[
          'flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap overflow-hidden',
          isExpanded ? 'gap-3' : 'justify-center',
          isActive('/measurement-requests')
            ? 'bg-white/15 text-white'
            : 'text-white/70 hover:bg-white/10 hover:text-white',
        ]"
        @click="handleNavClick($event, '/measurement-requests')"
      >
        <span class="plutto-icon plutto-icon--outlined shrink-0 text-xl">pending_actions</span>
        <span v-if="isExpanded">Mediciones pendientes</span>
      </a>

      <!-- Admin section -->
      <template v-if="auth.isAdmin">
        <div class="my-3 border-t border-white/20" />
        <span
          v-if="isExpanded"
          class="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-white/50"
        >
          Admin
        </span>
        <a
          v-for="item in adminNavItems"
          :key="item.path"
          :href="item.path"
          :title="isExpanded ? undefined : item.name"
          :class="[
            'flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap overflow-hidden',
            isExpanded ? 'gap-3' : 'justify-center',
            isActive(item.path)
              ? 'bg-white/15 text-white'
              : 'text-white/70 hover:bg-white/10 hover:text-white',
          ]"
          @click="handleNavClick($event, item.path)"
        >
          <span class="plutto-icon plutto-icon--outlined shrink-0 text-xl">{{ item.icon }}</span>
          <span v-if="isExpanded">{{ item.name }}</span>
        </a>
      </template>

      <!-- User section -->
      <div class="my-3 border-t border-white/20" />
      <span
        v-if="isExpanded"
        class="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-white/50"
      >
        Usuario
      </span>
      <a
        v-for="item in userNavItems"
        :key="item.path"
        :href="item.path"
        :title="isExpanded ? undefined : item.name"
        :class="[
          'flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap overflow-hidden',
          isExpanded ? 'gap-3' : 'justify-center',
          isActive(item.path)
            ? 'bg-white/15 text-white'
            : 'text-white/70 hover:bg-white/10 hover:text-white',
        ]"
        @click="handleNavClick($event, item.path)"
      >
        <span class="plutto-icon plutto-icon--outlined shrink-0 text-xl">{{ item.icon }}</span>
        <span v-if="isExpanded">{{ item.name }}</span>
      </a>
    </nav>

    <!-- Powered by Plutto -->
    <div
      v-if="isExpanded"
      class="border-t border-white/20 px-6 py-4"
    >
      <div class="flex items-center gap-2 text-white/50">
        <span class="text-xs">Powered by</span>
        <img
          :src="logoWhiteSrc"
          alt="Plutto"
          class="h-4 opacity-50"
        >
      </div>
    </div>
  </aside>

  <!-- Mobile overlay -->
  <div
    v-if="ui.sidebarOpen"
    data-testid="sidebar-overlay"
    class="fixed inset-0 z-20 bg-black/50 md:hidden"
    @click="ui.closeSidebar()"
  />
</template>
